import { guardPreview } from '@/lib/previewMode'
import { usePortalPermissions } from '@/hooks/usePortalPermissions'
import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { portalApi, type PortalStaffMemberDto } from '@/api/portal'
import { Users, Plus, Upload, Pencil, Check, ChevronRight } from 'lucide-react'
import { ResendInviteButton } from '@/components/shared/ResendInviteButton'
import { PortalStaffAvatar } from '@/features/photos/PhotoAvatars'

// ─── Helpers ──────────────────────────────────────────────────────────────────

// The only real distinction here is whether this person gets invited to log in to the
// portal — being a "ClientManager" grants no extra tier of admin, it just means "has an account".
const roleLabel = (r: string) => r === 'ClientManager' ? 'Portal access' : 'No login'
const hasPortalAccess = (r: string) => r === 'ClientManager'
const roleFromAccess = (checked: boolean) => checked ? 'ClientManager' : 'StaffMember'

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
      isActive ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
    }`}>
      {isActive ? 'Active' : 'Invited'}
    </span>
  )
}

function TrainingSummary({ records }: { records: Array<{ status: string }> }) {
  const overdue = records.filter(r => r.status === 'Overdue').length
  const dueSoon = records.filter(r => r.status === 'DueSoon').length
  const valid   = records.filter(r => r.status === 'Valid' || r.status === 'NoExpiry').length

  if (records.length === 0) return <span className="text-gray-400 text-sm">No records</span>

  return (
    <div className="flex gap-3 text-sm">
      {overdue > 0 && <span className="text-red-700 font-medium">{overdue} overdue</span>}
      {dueSoon > 0 && <span className="text-amber-700 font-medium">{dueSoon} due soon</span>}
      {overdue === 0 && dueSoon === 0 && (
        <span className="text-green-700 font-medium">{valid} current</span>
      )}
    </div>
  )
}

// ─── Add / Edit form (inline row) ─────────────────────────────────────────────

type StaffFormData = { firstName: string; lastName: string; email: string; role: string }

function StaffFormRow({
  initial,
  onSave,
  onCancel,
  pending,
  colSpan,
}: {
  initial: StaffFormData
  onSave: (data: StaffFormData) => void
  onCancel: () => void
  pending: boolean
  colSpan: number
}) {
  const [form, setForm] = useState<StaffFormData>(initial)

  return (
    <tr className="bg-[var(--brand-secondary)]/10">
      <td colSpan={colSpan} className="px-4 py-3">
        <form
          onSubmit={e => { e.preventDefault(); onSave(form) }}
          className="flex gap-3 items-end flex-wrap"
        >
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">First name</label>
            <input
              value={form.firstName}
              onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
              required
              className="h-9 w-32 rounded-[var(--brand-radius-sm)] border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">Last name</label>
            <input
              value={form.lastName}
              onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
              required
              className="h-9 w-32 rounded-[var(--brand-radius-sm)] border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
            />
          </div>
          <div className="space-y-1 flex-1 min-w-48">
            <label className="text-xs font-medium text-gray-500">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required
              className="h-9 w-full rounded-[var(--brand-radius-sm)] border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
            />
          </div>
          <label className="flex items-center gap-2 h-9 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={hasPortalAccess(form.role)}
              onChange={e => setForm(f => ({ ...f, role: roleFromAccess(e.target.checked) }))}
              className="h-4 w-4 rounded border-gray-300 text-[var(--brand-primary)]"
            />
            Portal access
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={pending}
              className="flex items-center gap-1.5 h-9 px-4 text-sm font-medium rounded-[var(--brand-radius-sm)] bg-[var(--brand-primary)] text-[var(--brand-primary-contrast)] hover:opacity-90 disabled:opacity-60 transition-opacity cursor-pointer"
            >
              <Check size={14} />
              {pending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="h-9 px-4 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </form>
        <p className="text-xs text-gray-400 mt-2">
          Portal access invites them by email to log in and view the whole team's training records and certificates.
        </p>
      </td>
    </tr>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const emptyForm: StaffFormData = { firstName: '', lastName: '', email: '', role: 'StaffMember' }

export default function TeamDashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const csvInputRef = useRef<HTMLInputElement>(null)
  const permissions = usePortalPermissions()

  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [importMessage, setImportMessage] = useState('')

  // Staff list (for management: roles, isActive, firstName/lastName)
  const { data: staff = [], isLoading: staffLoading } = useQuery({
    queryKey: ['portal-staff'],
    queryFn: () => portalApi.getStaff().then(r => r.data),
  })

  // Team training records (for training status column)
  const { data: team = [] } = useQuery({
    queryKey: ['team-training-records'],
    queryFn: () => portalApi.getTeamRecords().then(r => r.data),
  })

  // Index training records by staffMemberId for quick lookup
  const recordsByStaff = Object.fromEntries(team.map(m => [m.staffMemberId, m.records]))

  const addMutation = useMutation({
    mutationFn: (data: StaffFormData) => portalApi.addStaff(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-staff'] })
      queryClient.invalidateQueries({ queryKey: ['team-training-records'] })
      setShowAddForm(false)
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(detail ?? 'Failed to add staff member.')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: StaffFormData }) => portalApi.updateStaff(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-staff'] })
      setEditingId(null)
    },
    onError: () => alert('Failed to update staff member.'),
  })

  const importMutation = useMutation({
    mutationFn: (file: File) => portalApi.importStaff(file),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['portal-staff'] })
      queryClient.invalidateQueries({ queryKey: ['team-training-records'] })
      const { succeeded, failed, errors } = res.data
      if (failed > 0) {
        setImportMessage(`Imported ${succeeded}, failed ${failed}: ${errors.slice(0, 3).join('; ')}`)
      } else {
        setImportMessage(`${succeeded} staff member${succeeded === 1 ? '' : 's'} imported successfully.`)
      }
      setTimeout(() => setImportMessage(''), 6000)
    },
    onError: () => setImportMessage('Import failed. Please check your CSV format.'),
  })

  const handleCsvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) importMutation.mutate(file)
    e.target.value = ''
  }

  const toEditForm = (m: PortalStaffMemberDto): StaffFormData => ({
    firstName: m.firstName,
    lastName: m.lastName,
    email: m.email,
    role: m.role,
  })

  if (staffLoading) return <p className="text-gray-500">Loading…</p>

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users size={22} className="text-[var(--brand-primary)]" />
            My Team
          </h1>
          <p className="text-sm text-gray-500 mt-1">{permissions.staffManagement ? "Manage your organisation's staff and view their training" : "Your organisation's staff and their training"}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {permissions.staffManagement && (
            <>
            <button
              onClick={() => { if (!guardPreview()) csvInputRef.current?.click() }}
              disabled={importMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60 transition-colors cursor-pointer"
            >
              <Upload size={15} />
              <span className="hidden sm:inline">{importMutation.isPending ? 'Importing…' : 'Import CSV'}</span>
              <span className="sm:hidden">{importMutation.isPending ? '…' : 'CSV'}</span>
            </button>
            <input ref={csvInputRef} type="file" accept=".csv" className="hidden" onChange={handleCsvChange} />
            <button
              onClick={() => { if (guardPreview()) return; setShowAddForm(v => !v); setEditingId(null) }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-[var(--brand-radius-sm)] bg-[var(--brand-primary)] text-[var(--brand-primary-contrast)] hover:opacity-90 transition-opacity cursor-pointer"
            >
              <Plus size={15} />
              Add staff
            </button>
            </>
          )}
        </div>
      </div>

      {importMessage && (
        <p className={`text-sm px-4 py-2 rounded-lg border ${
          importMessage.includes('failed') || importMessage.includes('Failed')
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-green-50 border-green-200 text-green-700'
        }`}>
          {importMessage}
        </p>
      )}

      {/* ── Staff table ── */}
      {staff.length === 0 && !showAddForm ? (
        <div className="text-center py-16 text-gray-400">
          <Users size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No staff members yet.</p>
          {permissions.staffManagement && (
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-3 text-sm text-[var(--brand-primary)] hover:opacity-80 font-medium cursor-pointer"
            >
              Add your first staff member
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {/* Mobile: card list */}
          <div className="sm:hidden divide-y divide-gray-100">
            {showAddForm && (
              <div className="p-4">
                <StaffFormRow
                  initial={emptyForm}
                  onSave={data => addMutation.mutate(data)}
                  onCancel={() => setShowAddForm(false)}
                  pending={addMutation.isPending}
                  colSpan={1}
                />
              </div>
            )}
            {staff.map(member => (
              editingId === member.id ? (
                <div key={member.id} className="p-4">
                  <StaffFormRow
                    initial={toEditForm(member)}
                    onSave={data => updateMutation.mutate({ id: member.id, data })}
                    onCancel={() => setEditingId(null)}
                    pending={updateMutation.isPending}
                    colSpan={1}
                  />
                </div>
              ) : (
                <div
                  key={member.id}
                  className="px-4 py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-[var(--brand-secondary)]/10 transition-colors"
                  onClick={() => navigate(`/portal/team/${member.id}`)}
                >
                  <PortalStaffAvatar member={member} canEdit={permissions.staffManagement} size={36} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-900">{member.firstName} {member.lastName}</p>
                      <StatusBadge isActive={member.isActive} />
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{member.email}</p>
                    <div className="mt-1">
                      <TrainingSummary records={recordsByStaff[member.id] ?? []} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {permissions.staffManagement && (
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); if (guardPreview()) return; setEditingId(member.id); setShowAddForm(false) }}
                        className="h-8 w-8 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all cursor-pointer"
                        title="Edit"
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                    <ChevronRight size={15} className="text-gray-300" />
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Desktop: table */}
          <table className="hidden sm:table w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Access</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Training</th>
                <th className="w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {showAddForm && (
                <StaffFormRow
                  initial={emptyForm}
                  onSave={data => addMutation.mutate(data)}
                  onCancel={() => setShowAddForm(false)}
                  pending={addMutation.isPending}
                  colSpan={6}
                />
              )}
              {staff.map(member => (
                editingId === member.id ? (
                  <StaffFormRow
                    key={member.id}
                    initial={toEditForm(member)}
                    onSave={data => updateMutation.mutate({ id: member.id, data })}
                    onCancel={() => setEditingId(null)}
                    pending={updateMutation.isPending}
                    colSpan={6}
                  />
                ) : (
                  <tr
                    key={member.id}
                    className="hover:bg-[var(--brand-secondary)]/10 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/portal/team/${member.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      <div className="flex items-center gap-2.5">
                        <PortalStaffAvatar member={member} canEdit={permissions.staffManagement} />
                        {member.firstName} {member.lastName}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{member.email}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {roleLabel(member.role)}
                      {permissions.staffManagement && member.invitePending && <div><ResendInviteButton send={() => portalApi.resendStaffInvite(member.id)} /></div>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge isActive={member.isActive} />
                    </td>
                    <td className="px-4 py-3">
                      <TrainingSummary records={recordsByStaff[member.id] ?? []} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {permissions.staffManagement && (
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); if (guardPreview()) return; setEditingId(member.id); setShowAddForm(false) }}
                            className="h-7 w-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                            title="Edit"
                          >
                            <Pencil size={13} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); navigate(`/portal/team/${member.id}`) }}
                          className="h-7 w-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          <ChevronRight size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CSV format hint */}
      <p className="text-xs text-gray-400">
        CSV import format: <code className="font-mono bg-gray-100 px-1 rounded">email, first name, last name, role</code> — role is optional (defaults to Staff).
      </p>
    </div>
  )
}
